import { faSort, faXmark } from "@fortawesome/sharp-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import {
  Badge,
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from "@mui/material"
import type { GridSortDirection, GridSortModel } from "@mui/x-data-grid-pro"
import type React from "react"
import { useCallback, useMemo, useState } from "react"
import { extraColors } from "../../utils/theme"
import type { PrecomputedSortableColumn } from "./PrecomputedSortableColumns"

const SortPopover = styled(Box)(() => ({
  padding: 16,
  minWidth: 420,
  maxWidth: 520,
  backgroundColor: extraColors.dark5,
  border: `1px solid ${extraColors.translucent8}`,
  borderRadius: 12,
}))

const SortRuleRow = styled(Box, {
  shouldForwardProp: prop => prop !== "isDragging" && prop !== "isDropTarget",
})<{ isDragging?: boolean; isDropTarget?: boolean }>(
  ({ isDragging, isDropTarget }) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 0",
    opacity: isDragging ? 0.45 : 1,
    borderRadius: 8,
    outline: isDropTarget
      ? `1px dashed ${extraColors.status.navyLight}`
      : "none",
    outlineOffset: 2,
  })
)

const DragHandle = styled(IconButton)(() => ({
  cursor: "grab",
  color: extraColors.hint,
  "&:active": {
    cursor: "grabbing",
  },
}))

const filterSelectSx = {
  height: 32,
  bgcolor: extraColors.translucent5,
  borderRadius: "8px",
  fontWeight: 800,
  fontSize: "0.7rem",
  flex: 1,
} as const

const sortToggleSx = {
  height: 32,
  "& .MuiToggleButton-root": {
    px: 1.5,
    fontSize: "0.65rem",
    fontWeight: 800,
    color: extraColors.hint,
    borderColor: extraColors.translucent8,
    "&.Mui-selected": {
      color: extraColors.white,
      backgroundColor: extraColors.translucentActive,
    },
  },
} as const

const SortButton = styled(Button)(() => ({
  height: 32,
  fontWeight: 800,
  fontSize: "0.7rem",
  borderColor: extraColors.translucentWhite,
  color: extraColors.white,
  "&:hover": {
    borderColor: extraColors.status.navyLight,
    backgroundColor: extraColors.translucent8,
  },
}))

const getNextAvailableField = (
  sortableColumns: PrecomputedSortableColumn[],
  usedFields: Set<string>
): string | null => {
  const nextColumn = sortableColumns.find(
    column => !usedFields.has(column.field)
  )
  return nextColumn?.field ?? null
}

const updateSortRule = (
  sortModel: GridSortModel,
  index: number,
  patch: Partial<{ field: string; sort: GridSortDirection }>
): GridSortModel =>
  sortModel.map((item, itemIndex) =>
    itemIndex === index ? { ...item, ...patch } : item
  )

const reorderSortModel = (
  sortModel: GridSortModel,
  fromIndex: number,
  toIndex: number
): GridSortModel => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= sortModel.length ||
    toIndex >= sortModel.length
  ) {
    return sortModel
  }

  const nextSortModel = [...sortModel]
  const [movedItem] = nextSortModel.splice(fromIndex, 1)
  if (!movedItem) {
    return sortModel
  }
  nextSortModel.splice(toIndex, 0, movedItem)
  return nextSortModel
}

export const PrecomputedSortControl: React.FC<{
  sortModel: GridSortModel
  onChange: (sortModel: GridSortModel) => void
  sortableColumns: PrecomputedSortableColumn[]
  defaultSortModel: GridSortModel
}> = ({ sortModel, onChange, sortableColumns, defaultSortModel }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)
  const open = Boolean(anchorEl)

  const usedFields = useMemo(
    () => new Set(sortModel.map(item => item.field)),
    [sortModel]
  )

  const canAddSort =
    sortModel.length < sortableColumns.length &&
    getNextAvailableField(sortableColumns, usedFields) !== null

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setDraggedIndex(null)
    setDropTargetIndex(null)
  }

  const handleAddSort = useCallback(() => {
    const nextField = getNextAvailableField(sortableColumns, usedFields)
    if (!nextField) {
      return
    }

    onChange([...sortModel, { field: nextField, sort: "desc" }])
  }, [onChange, sortModel, sortableColumns, usedFields])

  const handleRemoveSort = useCallback(
    (index: number) => {
      onChange(sortModel.filter((_, itemIndex) => itemIndex !== index))
    },
    [onChange, sortModel]
  )

  const handleFieldChange = useCallback(
    (index: number, field: string) => {
      onChange(updateSortRule(sortModel, index, { field }))
    },
    [onChange, sortModel]
  )

  const handleDirectionChange = useCallback(
    (index: number, sort: GridSortDirection | null) => {
      if (!sort) {
        return
      }
      onChange(updateSortRule(sortModel, index, { sort }))
    },
    [onChange, sortModel]
  )

  const handleDragStart = useCallback(
    (index: number) => (event: React.DragEvent<HTMLButtonElement>) => {
      setDraggedIndex(index)
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", String(index))
    },
    []
  )

  const handleDragOver = useCallback(
    (index: number) => (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = "move"
      setDropTargetIndex(index)
    },
    []
  )

  const handleDrop = useCallback(
    (index: number) => (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      const fromIndex =
        draggedIndex ??
        Number.parseInt(event.dataTransfer.getData("text/plain"), 10)
      if (Number.isNaN(fromIndex)) {
        setDraggedIndex(null)
        setDropTargetIndex(null)
        return
      }
      onChange(reorderSortModel(sortModel, fromIndex, index))
      setDraggedIndex(null)
      setDropTargetIndex(null)
    },
    [draggedIndex, onChange, sortModel]
  )

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null)
    setDropTargetIndex(null)
  }, [])

  const handleResetToDefault = () => {
    onChange(defaultSortModel)
  }

  const handleClearAll = () => {
    onChange([])
  }

  const getFieldOptions = (currentField: string) =>
    sortableColumns.filter(
      column => column.field === currentField || !usedFields.has(column.field)
    )

  return (
    <>
      <Badge
        badgeContent={sortModel.length > 0 ? sortModel.length : undefined}
        color="primary"
        overlap="circular"
        invisible={sortModel.length === 0}
      >
        <SortButton
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faSort} />}
          onClick={handleOpen}
          aria-expanded={open}
          aria-haspopup="true"
        >
          Sort
        </SortButton>
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              background: "transparent",
              boxShadow: "none",
            },
          },
        }}
      >
        <SortPopover>
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: extraColors.hint,
              mb: 1,
            }}
          >
            Sort rules
          </Typography>

          {sortModel.length === 0 ? (
            <Typography
              sx={{ color: extraColors.hint, fontSize: "0.75rem", py: 1 }}
            >
              No sort applied. Add a rule or reset to default.
            </Typography>
          ) : (
            sortModel.map((item, index) => (
              <SortRuleRow
                key={item.field}
                isDragging={draggedIndex === index}
                isDropTarget={
                  dropTargetIndex === index && draggedIndex !== index
                }
                onDragOver={handleDragOver(index)}
                onDrop={handleDrop(index)}
              >
                <DragHandle
                  size="small"
                  draggable
                  aria-label={`Drag to reorder sort rule ${index + 1}`}
                  onDragStart={handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                >
                  <DragIndicatorIcon fontSize="small" />
                </DragHandle>
                <Typography
                  sx={{
                    width: 52,
                    flexShrink: 0,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: extraColors.hint,
                  }}
                >
                  {index === 0 ? "Sort by" : "Then by"}
                </Typography>
                <Select
                  size="small"
                  value={item.field}
                  onChange={event =>
                    handleFieldChange(index, event.target.value)
                  }
                  sx={filterSelectSx}
                >
                  {getFieldOptions(item.field).map(column => (
                    <MenuItem key={column.field} value={column.field}>
                      {column.label}
                    </MenuItem>
                  ))}
                </Select>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={item.sort ?? "desc"}
                  onChange={(_, value: GridSortDirection | null) =>
                    handleDirectionChange(index, value)
                  }
                  sx={sortToggleSx}
                >
                  <ToggleButton value="asc">Asc</ToggleButton>
                  <ToggleButton value="desc">Desc</ToggleButton>
                </ToggleButtonGroup>
                <IconButton
                  size="small"
                  aria-label={`Remove sort rule ${index + 1}`}
                  onClick={() => handleRemoveSort(index)}
                  sx={{ color: extraColors.hint }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              </SortRuleRow>
            ))
          )}

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={handleAddSort}
              disabled={!canAddSort}
              sx={{ fontWeight: 800, fontSize: "0.7rem" }}
            >
              + Add sort
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={handleResetToDefault}
              sx={{
                fontWeight: 800,
                fontSize: "0.7rem",
                color: extraColors.hint,
              }}
            >
              Reset to default
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={handleClearAll}
              sx={{
                fontWeight: 800,
                fontSize: "0.7rem",
                color: extraColors.hint,
              }}
            >
              Clear all
            </Button>
          </Stack>
        </SortPopover>
      </Popover>
    </>
  )
}
